
import api from './api';
import { Quote } from '../types';

const normalizeQuote = (raw: any): Quote => {
  const q = raw?.data ?? raw;
  const rawLegs = q?.legs || q?.Leg || [];
  const legServices = q?.quoteLegService || q?.QuoteLegService || [];
  const legs = Array.isArray(rawLegs)
    ? rawLegs.map((leg: any) => {
        const servicesForLeg = Array.isArray(legServices)
          ? legServices
              .filter((item: any) => item.legId === leg.id)
              .map((item: any) => ({
                serviceId: item.serviceId || undefined,
                code: item.serviceCode,
                name: item.serviceName,
                scope: item.scope,
                pricingModel: item.pricingModel,
                unitPrice: Number(item.unitPriceUSD) || 0,
                quantity: Number(item.quantity) || 1,
                source: item.source,
                contextCode: item.contextCode || null,
              }))
          : [];
        return {
          ...leg,
          extraServiceSelections: servicesForLeg,
        };
      })
    : [];
  const userName = [q?.User?.firstName, q?.User?.lastName].filter(Boolean).join(' ').trim();
  const issuedBy = userName || q?.issuedBy || '';
  const manifest = q?.PassengerManifest || null;
  const passengerList = Array.isArray(manifest?.Passengers)
    ? manifest.Passengers
    : Array.isArray(manifest?.passengers)
    ? manifest.passengers
    : [];
  const passengerCount = passengerList.length;
  return {
    ...q,
    legs,
    customerName: q?.customerName || q?.Customer?.name || 'Unknown',
    tailNumber: q?.tailNumber || q?.Aircraft?.tailNumber,
    aircraftModel: q?.aircraftModel || q?.Aircraft?.name || undefined,
    issuedBy: issuedBy || undefined,
    sentAt: q?.sentAt || undefined,
    passengerManifest: manifest
      ? {
          submittedAt: manifest.submittedAt || null,
          issuedAt: manifest.issuedAt || null,
          issuedBy: manifest.issuedBy || null,
          isIssued: !!manifest.issuedAt,
          passengerCount,
        }
      : null,
  } as Quote;
};

export const quoteApi = {
  getQuotes: async (): Promise<Quote[]> => {
    try {
      const response = await api.get('/quotes');
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data.map(normalizeQuote) : [];
    } catch (e) {
      console.error('Failed to fetch quotes:', e);
      return [];
    }
  },
  getQuote: async (id: string): Promise<Quote> => {
    const response = await api.get(`/quotes/${id}`);
    return normalizeQuote(response.data);
  },
  createQuote: async (quote: Partial<Quote>): Promise<Quote> => {
    const response = await api.post('/quotes', quote);
    return normalizeQuote(response.data);
  },
  updateStatus: async (id: string, status: string): Promise<Quote> => {
    const response = await api.patch(`/quotes/${id}/status`, { status });
    return normalizeQuote(response.data);
  },
  updateQuote: async (id: string, updates: Partial<Quote>): Promise<Quote> => {
    const response = await api.patch(`/quotes/${id}`, updates);
    return normalizeQuote(response.data);
  },
  sendQuote: async (
    id: string,
    payload: {
      includeBreakdown?: boolean;
      sendMode?: 'NEW' | 'SAME_THREAD';
      accountId?: string;
      htmlTemplateId?: string;
      pdfTemplateId?: string;
      to?: string;
      cc?: string;
      bcc?: string;
      threadMessageId?: string;
    } = {}
  ): Promise<Quote> => {
    const response = await api.post(`/quotes/${id}/send`, payload);
    return normalizeQuote(response.data);
  },
  refreshZohoEstimateStatus: async (id: string): Promise<Quote> => {
    const response = await api.get(`/quotes/${id}/zoho-estimate-status`);
    return normalizeQuote(response.data);
  },
  printQuote: async (
    id: string,
    payload: { includeBreakdown?: boolean; pdfTemplateId?: string } = {}
  ): Promise<Blob> => {
    const response = await api.get(`/quotes/${id}/pdf`, {
      params: payload,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  getPassengerManifest: async (id: string): Promise<any> => {
    const response = await api.get(`/quotes/${id}/manifest`);
    return response.data?.data || null;
  },
  issuePassengerManifest: async (id: string): Promise<any> => {
    const response = await api.post(`/quotes/${id}/manifest/issue`);
    return response.data?.data || null;
  },
  downloadPassengerManifestHtml: async (id: string, htmlTemplateId?: string): Promise<Blob> => {
    const response = await api.get(`/quotes/${id}/manifest/html`, {
      params: htmlTemplateId ? { htmlTemplateId } : undefined,
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  sendPassengerManifestEmail: async (
    id: string,
    payload?: {
      to?: string;
      cc?: string;
      bcc?: string;
      accountId?: string;
      htmlTemplateId?: string;
      subject?: string;
      html?: string;
      text?: string;
    }
  ): Promise<any> => {
    const response = await api.post(`/quotes/${id}/manifest/send-email`, payload || {});
    return response.data?.data || response.data;
  },
  getEmailThread: async (id: string): Promise<any[]> => {
    const response = await api.get(`/quotes/${id}/email-thread`);
    return response.data?.data || [];
  },
};
